import SortableTable from 'app/agriculture/components/Table/SortableTable';
import { Device } from 'app/agriculture/queries/devices/types/Device';
import { useMemo } from 'react';
import { Button } from 'app/agriculture/components/Button/Button';
import { TextWithIcon } from 'app/agriculture/components/TextWithIcon/TextWithIcon';
import { CircleIcon } from 'app/agriculture/icons';
import { DeviceDetailContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/DeviceDetailContextProvider';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { Skeleton } from '@mui/material';
import { DeviceStatusProvider } from 'app/agriculture/routes/Devices/utils/status/DeviceStatusContext';
import { StatHealth } from 'app/agriculture/components/StatHealth/StatHealth';
import { useSnapshots } from 'app/agriculture/queries/snapshots/useSnapshots';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { DeviceFlyToButton } from 'app/agriculture/routes/Devices/components/DeviceFlyToButton';
import { DynamicTemperatureIndicator } from 'app/agriculture/components/DynamicIcons/DynamicTemperatureIndicator/DynamicTemperatureIndicator';
import { DynamicRainIndicator } from 'app/agriculture/components/DynamicIcons/DynamicRainIndicator/DynamicRainIndicator';
import { useDailyRainfall } from 'app/agriculture/hooks/useDailyRainfall/useDailyRainfall';
import { useDailyRainfallValue } from 'app/agriculture/hooks/useDailyRainfall/useDailyRainfallValue';

interface WeatherStationListTableProps {
    devices: Device[];
    disableSort?: boolean;
    onSort?: () => void;
}

function DailyRainfallCell({ deviceId }: { deviceId: string }) {
    const dailyRainfall = useDailyRainfall({ deviceId });
    return (
        <TextWithIcon
            Icon={(props) => <DynamicRainIndicator rainfall={dailyRainfall} {...props} />}
        >
            {`${dailyRainfall?.toFixed(1) ?? '--'}mm`}
        </TextWithIcon>
    );
}

export function WeatherStationListTable({
    devices,
    disableSort = false,
    onSort = () => {
        /* do nothing */
    },
}: WeatherStationListTableProps) {
    const routeActions = useRouteActions();
    const weatherSnapshots = useSnapshots({
        snapshotType: 'Weather',
    });

    const { getDailyRainfallValue } = useDailyRainfallValue();

    const deviceData = useMemo(
        () =>
            devices.map((device) => {
                const { id, name } = device;
                const weatherSnapshot = weatherSnapshots.get(id) ?? null;

                return {
                    id,
                    name: {
                        value: name,
                        display: (
                            <div className="LevelMonitorList__name">
                                <SuspenseWrapper loadingComponent={null}>
                                    <DeviceFlyToButton deviceId={id} />
                                </SuspenseWrapper>
                                <Button
                                    variant="compact"
                                    onClick={() => {
                                        routeActions.goto(id, { type: 'Device' });
                                    }}
                                    text={name}
                                    className="LevelMonitorList__button"
                                />
                            </div>
                        ),
                    },
                    temp: {
                        value: weatherSnapshot?.temperature?.toFixed(1) ?? '--',
                        display: (
                            <TextWithIcon
                                Icon={(props) => (
                                    <DynamicTemperatureIndicator
                                        temperature={weatherSnapshot?.temperature ?? undefined}
                                        {...props}
                                    />
                                )}
                            >
                                {`${weatherSnapshot?.temperature?.toFixed(1) ?? '--'}°C`}
                            </TextWithIcon>
                        ),
                    },
                    rainfall: {
                        value: getDailyRainfallValue({ deviceId: device.id }) ?? 0,
                        display: (
                            <SuspenseWrapper>
                                <DailyRainfallCell deviceId={device.id} />
                            </SuspenseWrapper>
                        ),
                    },
                    status: {
                        value: '',
                        display: (
                            <DeviceDetailContext.Provider value={{ device }}>
                                <SuspenseWrapper
                                    loadingComponent={
                                        <StatDisplay
                                            Icon={(props) => (
                                                <CircleIcon {...props} size={10} color="grey" />
                                            )}
                                            value={<Skeleton variant="text" />}
                                        />
                                    }
                                >
                                    <DeviceStatusProvider deviceId={device.id}>
                                        <StatHealth hideLabel />
                                    </DeviceStatusProvider>
                                </SuspenseWrapper>
                            </DeviceDetailContext.Provider>
                        ),
                    },
                };
            }),
        [devices, weatherSnapshots, routeActions, getDailyRainfallValue],
    );

    return (
        <SortableTable
            button={({ id }) => ({
                text: 'Edit',
                onClick: () => {
                    routeActions.edit(id, { type: 'Device' });
                },
                startIcon: 'edit',
                variant: 'compact',
            })}
            data={deviceData}
            headCells={[
                { key: 'name', label: 'Name' },
                { key: 'temp', label: 'Temp' },
                { key: 'rainfall', label: 'Rain' },
                { key: 'status', label: 'Status' },
            ]}
            defaultSort={{ key: 'name', order: 'asc' }}
            disableSort={disableSort}
            onSort={onSort}
        />
    );
}
